import React from 'react';
import styled from 'styled-components';
import { 
  FaGraduationCap, 
  FaChartLine, 
  FaUsers, 
  FaAward,
  FaBookOpen,
  FaLaptop,
  FaCheckCircle,
  FaClock,
  FaFileAlt,
  FaMicroscope,
  FaHeartbeat,
  FaStethoscope
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 180px;
  min-height: calc(100vh - 140px);
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #0a4d68 0%, #088395 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 30%);
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  position: relative;
  
  span {
    color: #ffd966;
    display: block;
    font-size: 1.8rem;
    font-weight: 300;
    margin-top: 0.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
    
    span {
      font-size: 1.4rem;
    }
  }
`;

const HeroDescription = styled.p`
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5%;
`;

const Section = styled.div`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  color: #0a2b3c;
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 80px;
    height: 4px;
    background: #ff8c42;
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Grid2Col = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Grid3Col = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContentBlock = styled.div`
  p {
    color: #4a5f73;
    line-height: 1.8;
    margin-bottom: 1.5rem;
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(10, 77, 104, 0.9), transparent);
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  
  h3 {
    font-size: 1.3rem;
    margin-bottom: 0.3rem;
  }
  
  p {
    font-size: 0.9rem;
    opacity: 0.9;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(10, 77, 104, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(10, 77, 104, 0.1);
    border-color: #ff8c42;
  }
`;

const CardIcon = styled.div`
  font-size: 2.5rem;
  color: #ff8c42;
  margin-bottom: 1.5rem;
  background: rgba(255, 140, 66, 0.1);
  width: 70px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
`;

const CardTitle = styled.h3`
  font-size: 1.3rem;
  color: #0a2b3c;
  margin-bottom: 1rem;
  font-weight: 600;
`;

const CardText = styled.p`
  color: #5a6b7a;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  color: #4a5f73;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  
  svg {
    color: #ff8c42;
    font-size: 1.1rem;
    flex-shrink: 0;
  }
  
  strong {
    color: #0a2b3c;
    margin-right: 0.3rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin: 3rem 0;
  
  @media (max-width: 800px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 1.8rem 1rem;
  border-radius: 15px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(10, 77, 104, 0.05);
`;

const StatNumber = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  color: #0a4d68;
  margin-bottom: 0.3rem;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #5a6b7a;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const WhyChooseBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-left: 4px solid #ff8c42;
  padding: 2rem;
  border-radius: 10px;
  margin: 2rem 0;
  
  h3 {
    color: #0a2b3c;
    margin-bottom: 1rem;
    font-size: 1.3rem;
  }
  
  ul {
    list-style: none;
    padding: 0;
  }
  
  li {
    margin-bottom: 0.8rem;
    color: #4a5f73;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    
    svg {
      color: #ff8c42;
    }
  }
`;

const ODStraining = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>
          ODS Training
          <span>Oncology Data Specialist Certification</span>
        </HeroTitle>
        <HeroDescription>
          Master cancer registry management and become a certified Oncology Data Specialist 
          with our comprehensive training program
        </HeroDescription>
      </HeroSection>

      <Container>
        {/* What is ODS Section */}
        <Section>
          <Grid2Col>
            <ContentBlock>
              <SectionTitle>What is ODS?</SectionTitle>
              <p>
                <strong>Oncology Data Specialist (ODS)</strong> is a professional certification 
                offered by the National Cancer Registrars Association (NCRA) for individuals 
                specializing in cancer registry management and oncology data collection.
              </p>
              <p>
                ODS professionals are responsible for maintaining comprehensive cancer registries 
                that track cancer incidence, treatment, and outcomes. They play a crucial role in 
                cancer research, public health surveillance, and improving patient care through 
                accurate data collection and analysis.
              </p>
              <p>
                Certified Oncology Data Specialists are in high demand across hospitals, cancer 
                centers, research institutions, and public health agencies, making it one of the 
                most rewarding careers in the healthcare data field.
              </p>
            </ContentBlock>
            <ImageContainer>
              <img 
                src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Oncology Data Specialist at work" 
              />
              <ImageOverlay>
                <h3>Cancer Registry Management</h3>
                <p>Track, analyze, and report cancer data</p>
              </ImageOverlay>
            </ImageContainer>
          </Grid2Col>
        </Section>


        {/* Why Choose Us Section */}
        <Section>
          <SectionTitle>Why Choose Learn Thru Us Academy?</SectionTitle>
          <Grid3Col>
            <Card>
              <CardIcon><FaGraduationCap /></CardIcon>
              <CardTitle>Expert Instructors</CardTitle>
              <CardText>
                Learn from certified ODS professionals with 10+ years of experience in 
                cancer registry management and NCRA certification training.
              </CardText>
            </Card>
            <Card>
              <CardIcon><FaLaptop /></CardIcon>
              <CardTitle>Hands-on Training</CardTitle>
              <CardText>
                Practice with real cancer registry software and abstract actual cancer cases 
                under expert supervision.
              </CardText>
            </Card>
            <Card>
              <CardIcon><FaBookOpen /></CardIcon>
              <CardTitle>Comprehensive Curriculum</CardTitle>
              <CardText>
                Covering all aspects of cancer registry management, including AJCC staging, 
                SEER standards, and CoC requirements.
              </CardText>
            </Card>
          </Grid3Col>
        </Section>

        {/* Training Program Section */}
        <Section>
          <SectionTitle>Our ODS Training Program</SectionTitle>
          <Grid2Col>
            <ImageContainer>
              <img 
                src="https://images.unsplash.com/photo-1581056771107-24747f077703?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Medical training session" 
              />
            </ImageContainer>
            <ContentBlock>
              <p>
                At <strong>Learn Thru Us Academy</strong>, we've developed a comprehensive ODS 
                training program that goes beyond textbook learning. Our curriculum is designed 
                to provide you with practical skills and real-world experience that employers demand.
              </p>
              <List>
                <ListItem>
                  <FaCheckCircle /> <strong>120+ hours</strong> of intensive training
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> <strong>50+</strong> real cancer case abstracts
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> Access to <strong>cancer registry software</strong>
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> <strong>Mock exams</strong> with NCRA pattern
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> <strong>One-on-one mentoring</strong> sessions
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> <strong>Job placement</strong> assistance
                </ListItem>
              </List>
            </ContentBlock>
          </Grid2Col>
        </Section>

        {/* Curriculum Section */}
        <Section>
          <SectionTitle>What You'll Learn</SectionTitle>
          <Grid3Col>
            <Card>
              <CardIcon><FaMicroscope /></CardIcon>
              <CardTitle>Cancer Registry Operations</CardTitle>
              <CardText>
                Registry structure, casefinding, follow-up, data quality control, and 
                cancer program standards (CoC).
              </CardText>
            </Card>
            <Card>
              <CardIcon><FaHeartbeat /></CardIcon>
              <CardTitle>Cancer Staging & Coding</CardTitle>
              <CardText>
                AJCC staging, ICD-O-3 coding, SEER standards, and collaborative stage data 
                collection.
              </CardText>
            </Card>
            <Card>
              <CardIcon><FaFileAlt /></CardIcon>
              <CardTitle>Abstracting & Data Analysis</CardTitle>
              <CardText>
                Abstracting cancer cases, data validation, statistical analysis, and 
                reporting for research.
              </CardText>
            </Card>
          </Grid3Col>
        </Section>

        {/* How We Train Better */}
        <Section>
          <SectionTitle>How We Train Better</SectionTitle>
          <Grid2Col>
            <ContentBlock>
              <WhyChooseBox>
                <h3>Our Unique Approach</h3>
                <ul>
                  <li>
                    <FaCheckCircle /> <strong>Rely Methodology:</strong> Explain, Show, Do, Review - our proven four-step training approach
                  </li>
                  <li>
                    <FaCheckCircle /> <strong>Live Software Practice:</strong> Work directly with cancer registry software used in leading hospitals
                  </li>
                  <li>
                    <FaCheckCircle /> <strong>Real Case Studies:</strong> Practice with actual de-identified cancer cases from our partner institutions
                  </li>
                  <li>
                    <FaCheckCircle /> <strong>Expert Mentors:</strong> Get personalized guidance from certified ODS professionals
                  </li>
                  <li>
                    <FaCheckCircle /> <strong>Flexible Learning:</strong> Choose between classroom, online, or hybrid training modes
                  </li>
                  <li>
                    <FaCheckCircle /> <strong>Placement Support:</strong> Connect with our network of 100+ hiring partners
                  </li>
                </ul>
              </WhyChooseBox>
            </ContentBlock>
            <ImageContainer>
              <img 
                src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Students in training" 
              />
            </ImageContainer>
          </Grid2Col>
        </Section>

        {/* Career Opportunities */}
        <Section>
          <SectionTitle>Career Opportunities</SectionTitle>
          <Grid3Col>
            <Card>
              <CardTitle>Oncology Data Specialist</CardTitle>
              <CardText>
                Work in hospital cancer registries, maintain and analyze cancer data for 
                research and accreditation.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Cancer Registry Manager</CardTitle>
              <CardText>
                Lead registry operations, ensure data quality, and coordinate with 
                cancer committee and research teams.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Tumor Registrar</CardTitle>
              <CardText>
                Abstract and code cancer cases, follow up with patients, and maintain 
                complete cancer records.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Cancer Data Analyst</CardTitle>
              <CardText>
                Analyze cancer statistics, prepare reports, and support cancer research 
                and public health initiatives.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Clinical Data Specialist</CardTitle>
              <CardText>
                Work in clinical research, pharmaceutical companies, or contract research 
                organizations.
              </CardText>
            </Card>
            <Card>
              <CardTitle>Public Health Analyst</CardTitle>
              <CardText>
                Contribute to cancer surveillance programs at state and national health 
                agencies.
              </CardText>
            </Card>
          </Grid3Col>
        </Section>

        {/* Certification Preparation */}
        <Section>
          <Grid2Col>
            <ImageContainer>
              <img 
                src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                alt="Student studying for certification" 
              />
            </ImageContainer>
            <ContentBlock>
              <SectionTitle>NCRA Certification Preparation</SectionTitle>
              <p>
                Our program is specifically designed to prepare you for the <strong>NCRA ODS 
                certification exam</strong>. We cover all exam domains and provide extensive 
                practice materials.
              </p>
              <List>
                <ListItem>
                  <FaCheckCircle /> Comprehensive coverage of exam content areas
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> Practice exams with 500+ questions
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> Exam-taking strategies and tips
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> Weak area identification and focused improvement
                </ListItem>
                <ListItem>
                  <FaCheckCircle /> 94% of our students pass on first attempt
                </ListItem>
              </List>
            </ContentBlock>
          </Grid2Col>
        </Section>

        {/* Enrollment CTA */}
        <Section style={{ textAlign: 'center' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #0a4d68 0%, #088395 100%)',
            padding: '3rem',
            borderRadius: '20px',
            color: 'white'
          }}>
            <h2 style={{ fontSize: '2.2rem', marginBottom: '1rem' }}>Ready to Start Your ODS Journey?</h2>
            <p style={{ fontSize: '1.2rem', marginBottom: '2rem', opacity: 0.9 }}>
              Join our next batch and become a certified Oncology Data Specialist
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button style={{
                background: '#ff8c42',
                color: 'white',
                border: 'none',
                padding: '1rem 2.5rem',
                borderRadius: '50px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                Enroll Now
              </button>
              <button style={{
                background: 'transparent',
                color: 'white',
                border: '2px solid white',
                padding: '1rem 2.5rem',
                borderRadius: '50px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}>
                Download Brochure
              </button>
            </div>
          </div>
        </Section>
      </Container>
    </PageContainer>
  );
};

export default ODStraining;